/**
 * PRECIOS DE SUSCRIPCIÓN Y BONOS DE MINUTOS IA
 * ============================================
 * Fuente ÚNICA de verdad para TODAS las plataformas (centraldereservas.ai,
 * peluqueria.centraldereservas.ai, clinica.centraldereservas.ai,
 * tumesaya.centraldereservas.ai) y para el flujo in-app de compra de
 * minutos/suscripción.
 *
 * Los importes NO están aquí: viven en `utils/pricing.data.json`, que también
 * lee la guarda `scripts/check-pricing.mjs` (Node puro, sin TypeScript: el CI
 * corre Node 20 y no puede importar un `.ts`). Cambia el precio o los minutos
 * en el JSON y se refleja en todas las páginas.
 *
 * OJO 1: aquí sólo vive lo que se MUESTRA. El cobro real lo ejecuta Stripe
 * desde el backend, que tiene su propio catálogo (`PricingCatalogue`). Si
 * cambias una cifra de este fichero, hay que cambiarla en paralelo allí o la
 * app enseñará una cosa y cobrará/acreditará otra.
 *
 * OJO 2 (la trampa de este catálogo): el PVP (`priceCents`, SIN IVA) es la
 * única fuente de verdad. El €/min es un dato DERIVADO y REDONDEADO que NO
 * cuadra con `minutos × €/min` en cinco de los ocho productos (la suscripción
 * de 500 daría cien euros y cuesta ciento dos; la de 5000 daría setecientos
 * cincuenta y cuesta setecientos cuarenta y cuatro). Por
 * eso `ratePerMinuteLabel` es una CADENA con coma decimal: se pinta, no se
 * multiplica. Cualquier porcentaje de ahorro sale de `priceCents`, nunca del
 * label.
 */

import pricingData from './pricing.data.json'

/** Familia de producto: suscripción mensual o bono de pago único. */
export type ProductKind = 'subscription' | 'bono'

/** Un producto del catálogo, tal cual está en `pricing.data.json`. */
export interface PricingProduct {
  /** Identificador estable, el mismo que usa el backend (`sub_250`, `bono_500`…). */
  id: string
  kind: ProductKind
  /** Minutos de asistente IA que entrega el producto. */
  minutes: number
  /** PVP en céntimos, SIN IVA. Única fuente de verdad de un importe. */
  priceCents: number
  /**
   * €/min redondeado, formato español, p. ej. '0,22'. Dato de COMUNICACIÓN.
   * Es una cadena a propósito: no se puede multiplicar sin un `parseFloat`
   * explícito, que la guarda de lint prohíbe.
   */
  ratePerMinuteLabel: string
  /** Meses hasta la caducidad. Sólo en los bonos; las suscripciones no caducan. */
  expiryMonths?: number
}

const PRODUCTS = pricingData.products as readonly PricingProduct[]

/** Sufijo de IVA para añadir a los precios mostrados al usuario. */
export const IVA_LABEL = pricingData.ivaLabel

/**
 * Importe listo para pintar, SIN el símbolo €, p. ej. '55' o '55,50'.
 * Los ocho PVP del catálogo son euros exactos, así que en la práctica
 * devuelve el entero; el caso con decimales existe por si algún día no lo son.
 */
export function formatEur(cents: number): string {
  const euros = cents / 100
  return Number.isInteger(euros) ? String(euros) : euros.toFixed(2).replace('.', ',')
}

/** Separador de millares español: 1500 → '1.500'. Sin `Intl`, para que no dependa del ICU. */
function groupThousands(value: number): string {
  return String(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.')
}

/** Un tramo de suscripción mensual, con lo que hace falta para pintarlo. */
export interface SubscriptionTier extends PricingProduct {
  /** PVP mensual sin símbolo, p. ej. '102'. */
  priceLabel: string
  /** Minutos con separador de millares, p. ej. '2.500'. */
  minutesLabel: string
  /**
   * Ahorro por minuto frente al tramo de entrada, en % entero y calculado
   * SOBRE LOS PVP REALES (`priceCents`), nunca sobre el €/min redondeado.
   * 0 en el tramo de entrada.
   */
  savingsPercent: number
}

/** Un bono de minutos de pago único. */
export interface MinuteBono extends PricingProduct {
  priceLabel: string
  minutesLabel: string
  /** Los bonos SIEMPRE caducan; aquí el campo no es opcional. */
  expiryMonths: number
}

const SUBSCRIPTION_PRODUCTS = PRODUCTS.filter(product => product.kind === 'subscription')
const BONO_PRODUCTS = PRODUCTS.filter(product => product.kind === 'bono')

const entryTier = SUBSCRIPTION_PRODUCTS[0]
const largestBono = BONO_PRODUCTS[BONO_PRODUCTS.length - 1]

/** €/min real (no redondeado) de un producto. Sólo para comparar productos entre sí. */
function realRate(product: PricingProduct): number {
  return product.priceCents / product.minutes
}

/**
 * Los 5 tramos de suscripción mensual, de menor a mayor.
 * El argumento de venta es que cuanto más grande el tramo, más barato sale el
 * minuto: `savingsPercent` lo cuantifica a partir de los PVP.
 */
export const SUBSCRIPTION_TIERS: readonly SubscriptionTier[] = SUBSCRIPTION_PRODUCTS.map(product => ({
  ...product,
  priceLabel: formatEur(product.priceCents),
  minutesLabel: groupThousands(product.minutes),
  savingsPercent: Math.round((1 - realRate(product) / realRate(entryTier)) * 100)
}))

/** Meses que tardan en caducar los minutos de un bono (12). */
export const BONO_EXPIRY_MONTHS = largestBono.expiryMonths ?? 12

/** Los 3 bonos de minutos de pago único, de menor a mayor. */
export const MINUTE_BONOS: readonly MinuteBono[] = BONO_PRODUCTS.map(product => ({
  ...product,
  priceLabel: formatEur(product.priceCents),
  minutesLabel: groupThousands(product.minutes),
  expiryMonths: product.expiryMonths ?? BONO_EXPIRY_MONTHS
}))

/** Etiqueta de cabecera del catálogo, del estilo «desde N euros al mes + IVA». */
export const SUBSCRIPTION_FROM_LABEL = `desde ${formatEur(entryTier.priceCents)}€/mes ${IVA_LABEL}`

/**
 * La asimetría que el cliente va a notar («¿por qué caducan los del bono y no
 * los de la suscripción?»). Va JUNTO al botón de comprar el bono, no escondida
 * en un FAQ (reserva-ya#407).
 */
export const BONO_EXPIRY_ASYMMETRY_LABEL = `Los minutos de un bono caducan a los ${BONO_EXPIRY_MONTHS} meses. Los de tu suscripción no caducan mientras sigas siendo cliente.`

/**
 * Las seis reglas de las bolsas de minutos, tal como las fija booking-agent#431.
 * Están aquí y no copiadas en cada landing para que las cuatro marcas cuenten
 * exactamente lo mismo: si la web no las explica, las explica el teléfono.
 */
export const MINUTE_RULES: readonly string[] = [
  'Los minutos de tu suscripción no caducan y se acumulan sin límite mientras sigas siendo cliente.',
  'Si te das de baja, pierdes los minutos de suscripción que tuvieras acumulados.',
  `Los minutos de un bono caducan a los ${BONO_EXPIRY_MONTHS} meses desde la compra.`,
  'Se gastan primero los minutos de la suscripción y después los del bono.',
  'Un bono sólo se puede comprar teniendo la suscripción activa: es el colchón para el mes en que te quedas corto, no un producto de entrada.',
  `El bono más grande es de ${groupThousands(largestBono.minutes)} minutos; por encima de ahí se sube de tramo de suscripción.`
]

/** Suscripción: lo que no depende del tramo elegido. */
export const SUBSCRIPTION = {
  /** Etiqueta del periodo de facturación. */
  period: 'mes',
  /**
   * Días de prueba gratuita, iguales para todos los tipos de negocio.
   * No confundir con `signupBonusMinutes`: son 30 DÍAS de prueba y 30 MINUTOS
   * de regalo, dos cosas distintas que casualmente comparten número.
   */
  trialDays: 30,
  /**
   * Minutos de asistente IA de regalo al darse de alta.
   * Es de UNA SOLA VEZ, no se repite ni se suma cada mes: sirve para probar el
   * servicio antes de suscribirse.
   */
  signupBonusMinutes: 30
} as const

/**
 * Copy corto y reutilizable para el regalo de bienvenida (una sola vez).
 *
 * NOTA: aquí ya no vive ninguna etiqueta del tipo «55€/mes» ni «100 minutos
 * incluidos». Las tres que quedaban (`SUBSCRIPTION_LABEL`,
 * `SUBSCRIPTION_PRICE_DECIMAL`, `MONTHLY_MINUTES_LABEL`) describían la
 * suscripción única que ya no existe y apuntaban al tramo de entrada: en el
 * panel enseñaban el precio del tramo más pequeño a un cliente que podía tener
 * contratado cualquiera de los cinco. Lo que el panel pinta sale del CATÁLOGO
 * DEL BACKEND (`GET /pricing/catalogue/`), que es quien sabe qué tramo tiene
 * esta empresa; este fichero se queda con lo que es igual para todos.
 */
export const SIGNUP_BONUS_MINUTES_LABEL = `${SUBSCRIPTION.signupBonusMinutes} minutos de asistente IA gratis al darte de alta`
